import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  CircularProgress,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import API_BASE_URL from '../../../../../Constants';
import { 
  defaultDeviceForm,
  deviceGeneralInfo,
  deviceContractInfo,
  deviceOtherInfo,
  deviceClassToSubclassMap,
} from '../config/deviceConfig';
import DeviceAccordion from '../components/DeviceAccordion';
import { RenderFields } from '../components/RenderFields';

const DeviceEntryForm = ({ open, onClose, refreshDevices, buildings, customers, managementCompanies }) => {
  const [formData, setFormData] = useState(defaultDeviceForm);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [subclassOptions, setSubclassOptions] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedManagement, setSelectedManagement] = useState(null);


  const toggleExpand = () => setIsExpanded(!isExpanded);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => {
        const updatedFormData = { ...prev, [name]: type === 'checkbox' ? checked : value };

        // Handle buildingId changes
        if (name === 'buildingId') {
            const selectedBuilding = buildings.find((b) => b.buildingID === value);
            if (selectedBuilding) {
                const associatedCustomer = customers.find(
                    (c) => c.customerID === selectedBuilding.customerID
                );
                const associatedManagement = associatedCustomer
                    ? managementCompanies.find(
                          (m) => m.managementId === associatedCustomer.managementID
                      )
                    : null;
                // Update the customer and management company
                setSelectedCustomer(
                  associatedCustomer
                    ? {
                        customerId: associatedCustomer.customerID,
                        customerName: associatedCustomer.customerName,
                      }
                    : null
                );
                setSelectedManagement(
                    associatedManagement
                        ? {
                              managementId: associatedManagement.managementId,
                              managementName: associatedManagement.companyName,
                          }
                        : null
                );
                // Update formData with customerId and managementId
                updatedFormData.customerId = selectedBuilding.customerID || null;
                updatedFormData.managementId = associatedCustomer?.managementID || null;
            } else {
                // Clear customer and management if no building is selected
                setSelectedCustomer(null);
                setSelectedManagement(null);
                updatedFormData.customerId = null;
                updatedFormData.managementId = null;
            }
        }
        return updatedFormData;
    });
  };

  const validateFields = () => {
    const errors = {};

    console.log('Validating buildingId:', formData.buildingId);

    if (!formData.buildingId || formData.buildingId === '' || formData.buildingId === 0) {
      errors.buildingId = 'Building is required.';
    }

    setValidationErrors(errors);

    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    const isValid = validateFields();
    if (!isValid) {
      alert('Please fix the errors before saving.');
      return;
    }
  
    setIsSubmitting(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/setup/buildings/devices/assign`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
        credentials: 'include', // Equivalent to withCredentials: true
      });
  
      if (response.ok) {
        alert('Device added successfully');
        setFormData(defaultDeviceForm);
        onClose();
        refreshDevices();
      } else {
        throw new Error('Failed to add device');
      }
    } catch (error) {
      console.error('Error adding device:', error);
      alert('An error occurred while adding the device');
    } finally {
      setIsSubmitting(false);
    }
  };
  

  // Helper function to split fields array into two halves
  const splitFields = (fieldsArray) => {
    const midIndex = Math.ceil(fieldsArray.length / 2);
    return [fieldsArray.slice(0, midIndex), fieldsArray.slice(midIndex)];
  };

  const handleClassChange = (e) => {
    const selectedClass = e.target.value;
    // Update device data
    setFormData((prev) => ({
      ...prev,
      deviceClass: selectedClass,
      deviceSubclass: '', // Reset subclass when class changes
    }));
    // Update subclass options based on selected class
    setSubclassOptions(deviceClassToSubclassMap[selectedClass] || []);
  };

  const handleClose = () => {
    setFormData(defaultDeviceForm);
    onClose();
  }

  return (
    <Dialog
        open={open}
        onClose={handleClose}
        maxWidth={false}
        PaperProps={{
          style: {
            width: isExpanded ? '90%' : '1000px',
            maxHeight: '90vh',
            minHeight: '90vh',
          },
        }}
        fullWidth
        style={{ zIndex: 1400 }}
      >
      {/* Header */}
      <div className="sticky top-0 bg-white z-10">
        <div className="flex justify-between items-center p-4">
          <div className="flex items-center space-x-4 flex-grow">
            <button
              onClick={handleClose}
              className="p-2 rounded-full hover:bg-gray-200 transition"
              aria-label="Back"
            >
              <ArrowBackIcon />
            </button>
            <h2 className="text-2xl font-bold text-left">
              Add New Device
            </h2>
          </div>
          <button
              onClick={toggleExpand}
              className="p-2 rounded-full hover:bg-gray-200 transition"
              aria-label={isExpanded ? 'Shrink Modal' : 'Expand Modal'}
          >
            {isExpanded ? <FullscreenExitIcon /> : <FullscreenIcon />}
          </button>
        </div>
        <div className="flex space-x-4 p-4">
          <button
            onClick={handleSave}
            className="px-6 py-2 bg-sss-accent-yel text-sss-blue-sel font-bold rounded-md hover:bg-yellow-600 transition"
          >
            Save
          </button>
        </div>
      </div>

      {/* Content */}
      <DialogContent dividers>
        <div className="space-y-4">
          {/* General Information */}
          {(() => {
            const [generalInfoLeft, generalInfoRight] = splitFields(
              deviceGeneralInfo
            );
            return (
              <DeviceAccordion label="General Information">
                <div className={`flex ${
                        isExpanded ? 'flex-row' : 'flex-col md:flex-row'
                      } gap-4`}
                >
                  <div className="w-full">
                    <RenderFields
                      fields={generalInfoLeft}
                      formData={formData}
                      handleChange={handleInputChange}
                      handleClassChange={handleClassChange}
                      subclassOptions={subclassOptions}
                      readOnly={false}
                      buildings={buildings}
                      selectedCustomer={selectedCustomer}
                      selectedManagement={selectedManagement}
                      validationErrors={validationErrors}
                    />
                  </div>
                  <div className="w-full">
                    <RenderFields
                      fields={generalInfoRight}
                      formData={formData}
                      handleChange={handleInputChange}
                      handleClassChange={handleClassChange}
                      subclassOptions={subclassOptions}
                      readOnly={false}
                      buildings={buildings}
                      customers={customers}
                      managementCompanies={managementCompanies}
                      selectedCustomer={selectedCustomer}
                      selectedManagement={selectedManagement}
                      validationErrors={validationErrors}
                    />
                  </div>
                </div>
              </DeviceAccordion>
            );
          })()}

          {/* Contract Information Accordion */}
          {(() => {
            const [contractInfoLeft, contractInfoRight] = splitFields(
              deviceContractInfo
            );
            return (
              <DeviceAccordion label="Contract Information">
                <div
                  className={`flex ${
                    isExpanded ? 'flex-row' : 'flex-col md:flex-row'
                  } gap-4`}
                >
                  <div className="w-full">
                    <RenderFields
                      fields={contractInfoLeft}
                      formData={formData}
                      handleChange={handleInputChange}
                      readOnly={false}
                      buildings={buildings}
                      validationErrors={validationErrors}
                    />
                  </div>
                  <div className="w-full">
                    <RenderFields
                      fields={contractInfoRight}
                      formData={formData}
                      handleChange={handleInputChange}
                      readOnly={false}
                      buildings={buildings}
                      validationErrors={validationErrors}
                    />
                  </div>
                </div>
              </DeviceAccordion>
            );
          })()}

         

          {/* Other Info Accordion */}
          {(() => {
            const [otherInfoLeft, otherInfoRight] = splitFields(
              deviceOtherInfo
            );
            return (
              <DeviceAccordion label="Other Information">
                <div
                  className={`flex ${
                    isExpanded ? 'flex-row' : 'flex-col md:flex-row'
                  } gap-4`}
                >
                  <div className="w-full">
                    <RenderFields
                      fields={otherInfoLeft}
                      formData={formData}
                      handleChange={handleInputChange}
                      readOnly={false}
                      buildings={buildings}
                      validationErrors={validationErrors}
                    />
                  </div>
                  <div className="w-full">
                    <RenderFields
                      fields={otherInfoRight}
                      formData={formData}
                      handleChange={handleInputChange}
                      readOnly={false}
                      buildings={buildings}
                      validationErrors={validationErrors}
                    />
                  </div>
                </div>
              </DeviceAccordion>
            );
          })()}
        </div>
      </DialogContent>

      {isSubmitting && (
        <div className="absolute inset-0 flex justify-center items-center bg-white bg-opacity-75 z-50">
          <CircularProgress />
        </div>
      )}
    </Dialog>
  );
};

export default DeviceEntryForm;
